<template>
  <v-dialog :value="value" max-width="520px" @input="close">
    <v-card class="pa-6 pa-sm-8">
      <v-btn class="button-close ma-4" icon @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <h2 class="mb-6">Распределить из буфера</h2>

      <v-autocomplete
        v-model="form.corp_client"
        :items="clients"
        :loading="loadingData"
        label="Корпоративный клиент *"
        item-text="company"
        item-value="id"
        outlined dense
        :error-messages="fieldErrors.corp_client"
        no-data-text="За вами не закреплено корпоративных клиентов"
      />

      <v-autocomplete
        v-model="form.test"
        :items="testOptions"
        :loading="loadingData"
        label="Тест *"
        item-text="name"
        item-value="code"
        outlined dense
        :error-messages="fieldErrors.test"
        no-data-text="В буфере нет тестов"
      />

      <v-text-field
        v-model.number="form.amount"
        label="Количество *"
        type="number"
        min="1"
        outlined dense
        :hint="amountHint"
        persistent-hint
        :error-messages="fieldErrors.amount"
      >
        <template v-slot:append-outer>
          <v-btn small text color="primary" :disabled="!available" @click="form.amount = available">
            Весь остаток
          </v-btn>
        </template>
      </v-text-field>

      <v-alert v-if="serverErrors.length" type="error" dense outlined class="mt-4 mb-0">
        <div v-for="(err, ind) in serverErrors" :key="ind">{{ err }}</div>
      </v-alert>

      <div class="d-flex justify-end mt-6" style="gap: 12px">
        <v-btn text @click="close">Отмена</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="saving || loadingData" @click="submit">
          Распределить
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/common/api';

// Партнёр распределяет тесты из своего буфера закреплённым за ним корпам.
// Права, принадлежность корпа и остаток проверяет сервер; ошибки показываем в диалоге, не закрывая его.
export default {
  name: 'PartnerDistributeDialog',
  props: {
    value: { type: Boolean, default: false },
    partnerId: { type: [Number, String], required: true },
  },
  data: () => ({
    form: { corp_client: null, test: null, amount: null },
    fieldErrors: {},
    serverErrors: [],
    saving: false,
    loadingData: false,
    clients: [],
    balances: [],
  }),
  computed: {
    // Распределить можно только то, что есть в буфере
    testOptions() {
      return this.balances.filter((b) => b.amount > 0).map((b) => ({ code: b.code, name: b.name }));
    },
    available() {
      const found = this.balances.find((b) => b.code === this.form.test);
      return found ? found.amount : 0;
    },
    amountHint() {
      return this.form.test ? `В буфере: ${this.available}` : '';
    },
  },
  watch: {
    value(val) {
      if (!val) return;
      this.form = { corp_client: null, test: null, amount: null };
      this.fieldErrors = {};
      this.serverErrors = [];
      this.loadData();
    },
  },
  methods: {
    async loadData() {
      this.loadingData = true;
      try {
        const [stock, clients] = await Promise.all([
          api.get(`/users/partners/${this.partnerId}/get-stock-balance/`),
          api.get('/users/corp_clients/', { params: { page_size: -1 } }),
        ]);
        this.balances = (stock && stock.balance_detailed) || [];
        this.clients = Array.isArray(clients) ? clients : ((clients && clients.results) || []);
      } catch (e) {
        this.serverErrors = ['Не удалось загрузить остатки и список клиентов'];
      } finally {
        this.loadingData = false;
      }
    },
    validate() {
      const errors = {};
      const amount = Number(this.form.amount);
      if (!this.form.corp_client) errors.corp_client = ['Выберите клиента'];
      if (!this.form.test) errors.test = ['Выберите тест'];
      if (!Number.isInteger(amount) || amount < 1) {
        errors.amount = ['Целое число больше нуля'];
      } else if (this.form.test && amount > this.available) {
        errors.amount = [`В буфере только ${this.available}`];
      }
      this.fieldErrors = errors;
      return !Object.keys(errors).length;
    },
    async submit() {
      if (this.saving || !this.validate()) return;
      this.saving = true;
      this.serverErrors = [];
      const body = {
        test: this.form.test,
        amount: Number(this.form.amount),
        corp_client: Number(this.form.corp_client),
      };
      try {
        await api.post('/counters/partner-to-corpclient/', body);
        const client = this.clients.find((c) => c.id === body.corp_client);
        this.$toast.success(`Перечислено ${body.amount} шт.: ${client ? client.company : 'клиент'}`, '', { position: 'topRight' });
        this.$emit('success');
        this.$emit('input', false);
      } catch (err) {
        const data = (err && err.data) || {};
        const messages = [];
        Object.entries(data).forEach(([key, val]) => {
          const list = (Array.isArray(val) ? val : [val]).map((m) => (typeof m === 'string' ? m : JSON.stringify(m)));
          if (['test', 'amount', 'corp_client'].includes(key)) this.$set(this.fieldErrors, key, list);
          else messages.push(...list);
        });
        if (!messages.length && !Object.keys(this.fieldErrors).length) messages.push('Не удалось выполнить операцию');
        this.serverErrors = messages;
        // Остаток мог измениться параллельно: подтягиваем актуальный
        this.loadData();
      } finally {
        this.saving = false;
      }
    },
    close() {
      if (!this.saving) this.$emit('input', false);
    },
  },
};
</script>
